/**
 * The sales reports `/v1/merchant/reports` answers, as the server shapes them.
 *
 * Mirrors `merchant-report.dto.ts` on the API. Every figure is computed there —
 * totals, subtotals, shares, the order discount spread across lines — and this
 * layer only lays them out. A browser re-adding a column it was already handed
 * the total of is how two screens end up a cent apart on the same day.
 *
 * Fields marked optional arrived after the first cut of the module (invoice
 * numbers, order-line snapshots, the audit log's links to orders). A server one
 * release behind simply leaves them out, and every screen reads that as "not
 * recorded" rather than breaking.
 */

import type { LineBlock } from '../line-groups'

/** Which orders a report counts. Omitted means both. */
export const ReportChannel = {
  /** QR ordering — the customer's own phone. */
  Qr: 'NEXQR',
  Pos: 'POS',
} as const

export type ReportChannel = (typeof ReportChannel)[keyof typeof ReportChannel]

export const SalesSummaryGroupBy = {
  Day: 'day',
  Month: 'month',
} as const

export type SalesSummaryGroupBy = (typeof SalesSummaryGroupBy)[keyof typeof SalesSummaryGroupBy]

/** What every report is asked for. Dates are Phnom Penh trading days, inclusive. */
export interface ReportQuery {
  /** `YYYY-MM-DD`, the first trading day. */
  from: string
  /** `YYYY-MM-DD`, the last trading day — the whole of it counts. */
  to: string
  branchIds?: string[]
  channel?: ReportChannel
}

export interface SalesSummaryQuery extends ReportQuery {
  groupBy?: SalesSummaryGroupBy
}

/**
 * The blocks the item report lists its items in. `employee` is the cashier who
 * took the money — the merchant's decision that a sale belongs to the cashier,
 * so there is no separate waiter or salesperson.
 */
export const ItemSalesGroupBy = {
  Category: 'category',
  Employee: 'employee',
  Customer: 'customer',
} as const

export type ItemSalesGroupBy = (typeof ItemSalesGroupBy)[keyof typeof ItemSalesGroupBy]

export interface ItemSalesQuery extends ReportQuery {
  groupBy?: ItemSalesGroupBy
}

/** The blocks the invoice-lines workbook lists its lines in. The JSON read stays flat. */
export interface InvoiceLinesExportQuery extends ReportQuery {
  groupBy?: LineBlock
}

export interface AuditLogQuery extends ReportQuery {
  action?: string
  outcome?: string
}

/** What every report says about itself — the header of its printout. */
export interface ReportHeader {
  title: string
  merchantName: string
  /** Outlets that had activity in the period. */
  branches: string[]
  from: string
  to: string
  timezone: string
  /** ISO instant the figures were read at. */
  generatedAt: string
  /** The period reaches today, so its last day can still change. */
  includesToday: boolean
}

/**
 * The money columns every sales report shares, in the order the legacy pack
 * printed them: Total → Discount → After discount → VAT → Delivery → Net.
 */
export interface ReportMoney {
  totalAmount: number
  discount: number
  afterDiscount: number
  vat: number
  /** The delivery fee. NexQR has no service charge. */
  delivery: number
  net: number
}

/* -------------------------------------------------------------------------- */
/* R1 — Sales summary                                                         */
/* -------------------------------------------------------------------------- */

export interface SalesSummaryRow extends ReportMoney {
  branchId: string | null
  branchName: string
  /** `YYYY-MM-DD` or `YYYY-MM`. Null on a subtotal and on the grand total. */
  period: string | null
  invoices: number
  averageTicket: number
  refundCount: number
  refundAmount: number
}

export interface SalesSummaryBranch {
  branchId: string
  branchName: string
  rows: SalesSummaryRow[]
  subtotal: SalesSummaryRow
}

export interface SalesSummaryReport {
  meta: ReportHeader
  groupBy: SalesSummaryGroupBy
  branches: SalesSummaryBranch[]
  total: SalesSummaryRow
}

/* -------------------------------------------------------------------------- */
/* R15 — Sales register                                                       */
/* -------------------------------------------------------------------------- */

export const ReportSaleKind = {
  Sale: 'sale',
  Refund: 'refund',
} as const

export type ReportSaleKind = (typeof ReportSaleKind)[keyof typeof ReportSaleKind]

/**
 * Which columns the register prints — the legacy pack's "Sales Register
 * Summary" against its "Summary All Columns".
 *
 * The short one is the one read on screen: who, when, and what it came to. The
 * long one adds how it was paid and the cash that crossed the counter, which is
 * what a reconciliation needs and what makes the table scroll sideways.
 */
export const RegisterColumns = {
  Summary: 'summary',
  All: 'all',
} as const

export type RegisterColumns = (typeof RegisterColumns)[keyof typeof RegisterColumns]

export interface SalesRegisterRow extends ReportMoney {
  /** When the sale settled. */
  at: string
  /** Server-issued, sequential per outlet. Null on orders from before it existed. */
  invoiceNumber?: string | null
  /** The till's or the QR checkout's own reference. */
  orderNumber: string
  branchName: string
  channel: string
  customerName: string
  cashierName: string | null
  /** The table it was served at. Null on takeaway and delivery. */
  tableName: string | null
  /** The drawer session it was rung up in (`S-1042`) — the legacy "Shift No.". Null on QR orders. */
  shiftNumber: string | null
  paymentMethod: string
  /** What the customer handed over, in USD — riel at the rate the till recorded. Net on anything but cash. */
  totalReceived: number
  /** What was handed back, in USD: `totalReceived` less `net`. */
  changeGiven: number
  /** Cash handed over, in its own currency. */
  cashReceived: number | null
  cashReceivedCurrency: string | null
  change: number | null
  changeCurrency: string | null
  kind: ReportSaleKind
}

/** A run of invoice numbers the period skipped, within one outlet. */
export interface InvoiceNumberGap {
  branchName: string
  from: string
  to: string
  missing: number
}

export interface SalesRegisterTotal extends ReportMoney {
  invoices: number
  /** USD, sales only. Less `changeGiven`, it is `net`. */
  totalReceived: number
  changeGiven: number
  refundCount: number
  refundAmount: number
}

export interface SalesRegisterReport {
  meta: ReportHeader
  rows: SalesRegisterRow[]
  total: SalesRegisterTotal
  numberGaps?: InvoiceNumberGap[]
}

/* -------------------------------------------------------------------------- */
/* R2 — Item sales                                                            */
/* -------------------------------------------------------------------------- */

export interface ItemSalesRow {
  sku: string | null
  name: string
  /** The variant or box size the line was sold as, when the product has them. */
  variantName?: string | null
  unit: string | null
  quantity: number
  /** Given away — the legacy FOC. */
  compQuantity: number
  /** Menu value of what was given away. */
  compValue: number
  totalAmount: number
  discount: number
  /** Before VAT and delivery, which belong to the order rather than to an item. */
  net: number
  /** Share of the period's item net, 0–1. */
  shareOfSales: number
  /**
   * What the units cost — kept and comped — at the cost each line was sold at.
   * Null when none of them had a cost: never 0 for "unknown".
   */
  cost?: number | null
  /** Net of the costed lines less their cost. Null when no line had a cost. */
  margin?: number | null
  /** `margin` over the net of the costed lines, 0–1. */
  marginPercent?: number | null
  /** Share of `net` the margin speaks for, 0–1. Below 1, some sales had no cost. */
  costCoverage?: number
  /** What the units sold earned the cashier. Comps earn nothing. */
  incentive?: number
}

export interface ItemSalesGroup {
  /** The category, cashier or customer, per the report's `groupBy`. Absent from a server from before it. */
  name?: string
  /** Same as `name`; the only name a server from before `groupBy` sends. */
  categoryName: string
  items: ItemSalesRow[]
  subtotal: ItemSalesRow
}

export interface ItemSalesReport {
  meta: ReportHeader
  groupBy?: ItemSalesGroupBy
  groups: ItemSalesGroup[]
  total: ItemSalesRow
  itemCount: number
}

/* -------------------------------------------------------------------------- */
/* R5 — Invoice lines                                                         */
/* -------------------------------------------------------------------------- */

/** One item on one invoice. The details reports are these lines put in blocks. */
export interface InvoiceLine {
  /** Joins to `InvoiceTotal.orderId`. */
  orderId: string
  /** When the sale settled. */
  at: string
  invoiceNumber: string | null
  orderNumber: string
  branchName: string
  channel: string
  customerName: string
  cashierName: string | null
  categoryName: string
  sku: string | null
  name: string
  variantName: string | null
  unit: string | null
  quantity: number
  compQuantity: number
  compValue: number
  totalAmount: number
  discount: number
  /** Before VAT and delivery. Adds up to the item report's net. */
  net: number
  /** Null when the line was sold without a cost — never 0. */
  cost: number | null
  margin: number | null
  incentive: number
}

/** An invoice's own money, to print under its lines. */
export interface InvoiceTotal extends ReportMoney {
  orderId: string
  paymentMethod: string
}

export interface InvoiceLinesReport {
  meta: ReportHeader
  /** Sales only, oldest first. Refunds are not lines. */
  lines: InvoiceLine[]
  invoices: InvoiceTotal[]
}

/* -------------------------------------------------------------------------- */
/* R16 — Settlement                                                           */
/* -------------------------------------------------------------------------- */

/** Cash as it was handed over, split by the currency it arrived in. Riel is never converted. */
export interface CashTender {
  receivedUsd: number
  receivedKhr: number
  changeUsd: number
  changeKhr: number
}

export interface SettlementInvoice extends CashTender {
  at: string
  invoiceNumber?: string | null
  orderNumber: string
  branchName: string
  cashierName: string | null
  net: number
  /** Keyed by `paymentMethods[].key`. */
  byMethod: Record<string, number>
}

export interface SettlementDay extends ReportMoney {
  /** `YYYY-MM-DD`. Null on the total row. */
  date: string | null
  invoices: number
  /** Item net per category. Keyed by `categories`. */
  byCategory: Record<string, number>
  byMethod: Record<string, number>
  cash: CashTender
  refundAmount: number
}

export interface PaymentMethodColumn {
  key: string
  label: string
}

export interface SettlementReport {
  meta: ReportHeader
  /** Only the methods used in the period, biggest first. */
  paymentMethods: PaymentMethodColumn[]
  /** Only the categories sold in the period. */
  categories: string[]
  invoices: SettlementInvoice[]
  days: SettlementDay[]
  total: SettlementDay
}

/* -------------------------------------------------------------------------- */
/* R17 — Audit log                                                            */
/* -------------------------------------------------------------------------- */

/** A line as it stood either side of an approval. Only what changed is present. */
export interface AuditLineState {
  price?: number
  quantity?: number
  discountPercent?: number
  discountAmount?: number
  net?: number
}

export interface AuditLogRow {
  at: string
  action: string
  actionLabel: string
  branchName: string
  registerName: string | null
  requestedBy: string | null
  approvedBy: string | null
  invoiceNumber?: string | null
  orderNumber: string | null
  itemName: string | null
  before: AuditLineState | null
  after: AuditLineState | null
  amount: number | null
  percentage: number | null
  outcome: string
  outcomeLabel: string
  /** The till decided alone because it could not reach anybody. */
  offline: boolean
  note: string | null
}

export interface AuditLogTotal {
  count: number
  approved: number
  denied: number
  offline: number
}

export interface AuditLogReport {
  meta: ReportHeader
  rows: AuditLogRow[]
  total: AuditLogTotal
  /** Only the actions present in the period — what the action filter offers. */
  actions: { key: string; label: string }[]
}
